/**
 * WorldEnvModule — mirrors the current world's `world_environment` row (the
 * Edit Mode ENVIRONMENT panel) into `lib/world_env` and pokes the lighting /
 * fog / weather / ambience modules (`atlas:environment-updated`) whenever it
 * changes, so an editor's Apply is live for every visitor within a replication
 * round-trip. The server already steers the sky clock/weather from the same
 * row; this module only carries the client-side look (fog, sun, ambience).
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { setWorldEnv, worldEnv } from '../lib/world_env.js';

export class WorldEnvModule implements IModule {
  readonly name = 'WorldEnv';
  private bound = false;

  init(app: App): void {
    void app;
    bus.on('atlas:account-ready', () => this.bind());
    bus.on('atlas:world-entered', () => this.apply());
    bus.on('atlas:world-cleared', () => { if (setWorldEnv(null)) bus.emit('atlas:environment-updated', {}); });
    (window as unknown as Record<string, unknown>).__worldEnv = () => worldEnv();
  }

  private bind(): void {
    const conn = atlas.conn;
    if (!conn || this.bound) return;
    this.bound = true;
    // audio_zone rides along: AtlasAudioModule plays zones from the row cache but
    // nothing had ever subscribed the table (dormant since G-audio) — visitors
    // now hear the zones the editor places.
    try { conn.subscriptionBuilder().onApplied(() => this.apply()).subscribe(['SELECT * FROM world_environment', 'SELECT * FROM audio_zone']); } catch { /* pre-publish */ }
    const resync = (): void => this.apply();
    conn.db.worldEnvironment.onInsert(resync);
    conn.db.worldEnvironment.onUpdate(resync);
    conn.db.worldEnvironment.onDelete(resync);
    this.apply();
  }

  private apply(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    let row: ReturnType<typeof conn.db.worldEnvironment.worldId.find> | undefined;
    try { row = conn.db.worldEnvironment.worldId.find(wid); } catch { row = undefined; }
    const changed = setWorldEnv(row ? {
      timeMode: row.timeMode as 'cycle' | 'fixed', fixedTimeOfDay: row.fixedTimeOfDay, weatherMode: row.weatherMode as 'auto' | 'fixed',
      fogMul: row.fogMul, fogColorHex: row.fogColorHex, sunMul: row.sunMul, ambientMul: row.ambientMul, sunTintHex: row.sunTintHex,
      ambienceKey: row.ambienceKey, ambienceGain: row.ambienceGain,
    } : null);
    if (changed) bus.emit('atlas:environment-updated', {});
  }
}
